/**
 * Hash-based routing: parsing the URL hash into a Route and serializing it back.
 * Kept separate from App.tsx so the URL round-trip is independently testable
 * without pulling in the full component/auth/env dependency graph.
 */

import type { ExtensionType } from "../types/index";

export type Route =
  | { page: "catalog"; filterClients?: string[]; filterCategories?: string[]; filterQuery?: string; filterType?: ExtensionType; filterOfficial?: boolean }
  | { page: "detail"; installKey: string }
  | { page: "contribute" }
  | { page: "edit"; installKey: string }
  | { page: "admin" }
  | { page: "help" };

const VALID_CLIENT_VALUES = new Set(["claude-code", "claude-cowork", "claude-chat", "copilot"]);
const LEGACY_CLIENT_URL_MAP: Record<string, string> = {
  "claude-desktop": "claude-chat",
  "cowork":         "claude-cowork",
};

function parseClientParam(raw: string): string | null {
  const remapped = LEGACY_CLIENT_URL_MAP[raw] ?? raw;
  if (VALID_CLIENT_VALUES.has(remapped)) return remapped;
  console.warn(`[parseHash] Unrecognized client URL value "${raw}" discarded`);
  return null;
}

export function parseHash(hash: string): Route {
  const [pathPart, queryPart] = hash.replace(/^#\/?/, "").split("?");
  const path = pathPart ?? "";
  if (path === "contribute") return { page: "contribute" };
  if (path === "help") return { page: "help" };
  if (path === "admin") return { page: "admin" };
  if (path.startsWith("edit/")) {
    const installKey = decodeURIComponent(path.slice("edit/".length));
    if (installKey) return { page: "edit", installKey };
  }
  if (path.startsWith("skill/")) {
    const installKey = decodeURIComponent(path.slice("skill/".length));
    if (installKey) return { page: "detail", installKey };
  }
  const params = new URLSearchParams(queryPart ?? "");
  const typeParam = params.get("type");
  return {
    page: "catalog",
    filterClients: params.get("client")
      ?.split(",")
      .filter(Boolean)
      .map(parseClientParam)
      .filter((v): v is string => v !== null) ?? undefined,
    filterCategories: params.get("category")?.split(",").filter(Boolean) ?? undefined,
    filterQuery: params.get("q") ?? undefined,
    filterType: typeParam === "plugin" || typeParam === "skill" ? typeParam : undefined,
    filterOfficial: params.get("official") === "1" ? true : undefined,
  };
}

export function routeToHash(route: Route): string {
  switch (route.page) {
    case "catalog": {
      const params = new URLSearchParams();
      if (route.filterClients?.length) params.set("client", route.filterClients.join(","));
      if (route.filterCategories?.length) params.set("category", route.filterCategories.join(","));
      if (route.filterQuery) params.set("q", route.filterQuery);
      if (route.filterType) params.set("type", route.filterType);
      if (route.filterOfficial) params.set("official", "1");
      const qs = params.toString();
      return qs ? `#/?${qs}` : "#/";
    }
    case "contribute": return "#/contribute";
    case "help": return "#/help";
    case "admin": return "#/admin";
    case "detail": return `#/skill/${encodeURIComponent(route.installKey)}`;
    case "edit": return `#/edit/${encodeURIComponent(route.installKey)}`;
  }
}
